import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import SearchBar from '../components/SearchBar';
import FilterBar from '../components/FilterBar';
import AlertBanner from '../components/AlertBanner';
import VehicleCard from '../components/VehicleCard';
import { analytics } from '../services/analytics';
import { vehicleService } from '../services/api';
import './ResultsPage.css';

const ResultsPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('recommended');
  const [filters, setFilters] = useState({
    priceRange: null,
    passengers: null,
    category: null
  });

  const loadVehicles = useCallback(async () => {
    try {
      setLoading(true);
      const params = Object.fromEntries(searchParams);
      const response = await vehicleService.search(params);
      setVehicles(response.data.data || []);
    } catch (error) {
      console.error('Error loading vehicles:', error);
      setVehicles([]);
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  useEffect(() => {
    analytics.trackPageView('results_page');
    loadVehicles();
  }, [loadVehicles]);

  const handleSearch = (searchData) => {
    analytics.trackSearch(searchData);
    const params = new URLSearchParams(searchData);
    navigate(`/buscar?${params.toString()}`);
  };

  const handleSortChange = (newSort) => {
    setSortBy(newSort);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  // Aplicar filtros
  const filteredVehicles = vehicles.filter(vehicle => {
    // Filtro de precio
    if (filters.priceRange) {
      const [min, max] = filters.priceRange.split('-').map(Number);
      const price = vehicle.pricePerDay || vehicle.price;
      if (price < min || price > max) return false;
    }

    // Filtro de pasajeros
    if (filters.passengers) {
      if (vehicle.features?.passengers !== filters.passengers) return false;
    }

    // Filtro de categoría
    if (filters.category) {
      if (vehicle.category !== filters.category) return false;
    }

    return true;
  });

  // Aplicar ordenamiento
  const sortedVehicles = [...filteredVehicles].sort((a, b) => {
    const priceA = a.pricePerDay || a.price;
    const priceB = b.pricePerDay || b.price;

    switch (sortBy) {
      case 'price-low':
        return priceA - priceB;
      case 'price-high':
        return priceB - priceA;
      default:
        return 0;
    }
  });

  return (
    <div className="results-page">
      <div className="container">
        <SearchBar 
          onSearch={handleSearch}
          initialValues={Object.fromEntries(searchParams)}
        />

        <AlertBanner
          message="Quedan pocas unidades!"
          submessage="Últimas unidades disponibles para estas fechas! Reservá ahora y asegurá tu vehículo"
        />

        <FilterBar
          resultCount={sortedVehicles.length}
          sortBy={sortBy}
          onSortChange={handleSortChange}
          filters={filters}
          onFilterChange={handleFilterChange}
        />
        
        {loading ? (
          <div className="loading">Cargando vehículos...</div>
        ) : sortedVehicles.length > 0 ? (
          <div className="vehicles-list">
            {sortedVehicles.map(vehicle => (
              <VehicleCard key={vehicle.id} vehicle={vehicle} />
            ))}
          </div>
        ) : (
          <div className="no-results">
            <p>No se encontraron vehículos con los filtros seleccionados.</p>
            <button 
              className="clear-filters-btn"
              onClick={() => setFilters({ priceRange: null, passengers: null, category: null })}
            >
              Limpiar filtros
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResultsPage;
